/**
 * PatientPortalAdmin.tsx — Admin > Account > Patient Portal.
 *
 * Legacy's Patient Portal (admin/admin_portal_list.php) for the signed-in
 * account, with legacy's labels:
 *   - Settings: Status, Invitation Subject, Invitation Message (the email
 *     editor), Portal Footer, Welcome Disclaimer and its Disclaimer Message,
 *     Verification Required (date of birth, cell phone). Save sits top right,
 *     with Unsaved changes and Revert, as on the other settings pages.
 *   - Links on the portal's dashboard: Title, Form / URL and Opens in, in the
 *     account's order (drag a grip; deleted links hidden); "Add link"; a click
 *     opens a link; Delete / Restore. A link: Title *, URL, Form (a portal
 *     form or a Formstack form), Custom Content, Opens in.
 * A practice without a portal row cannot save anything here (legacy could not
 * either); the page says so.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): the settings sit in an
 * AdminLockedFieldset and show read-only, with no Save or Revert; the links
 * have no Add link, drag, row icons or Actions column, Show deleted still
 * works, and a click opens a link read-only (AdminFormModal's viewer).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Plus, RotateCcw } from 'lucide-react';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListTable,
  AdminRowName,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { RichTextEditor } from '@/components/admin/RichTextEditor';
import { ADMIN_DRAG_COLUMN, SortableAdminRow, useAdminDragSensors } from '@/components/admin/SortableAdminRow';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { getErrorMessage } from '@/services/apiClient';
import {
  INVITE_SUBJECT_MAX,
  PORTAL_LINK_TITLE_MAX,
  createPortalLink,
  deletePortalLink,
  getPortalAdmin,
  reorderPortalLinks,
  restorePortalLink,
  savePortalSettings,
  updatePortalLink,
  type PortalAdmin,
  type PortalLink,
  type PortalSettings,
} from '@/services/patientPortalAdmin.service';
import { toast } from '@/store/toastStore';

const EMPTY: PortalSettings = {
  enabled: false,
  inviteSubject: '',
  inviteMessage: '',
  footerContent: '',
  welcDisclaimerActive: false,
  welcDisclaimer: '',
  dobRequired: false,
  cellphoneRequired: false,
};

const ON_OFF = [
  { value: 'on', label: 'Enabled' },
  { value: 'off', label: 'Disabled' },
];

const OPENS_IN = [
  { value: 'same', label: 'same window' },
  { value: 'new', label: 'new window' },
];

const LINK_COLUMNS: AdminListColumn[] = [
  { key: 'title', header: 'Title' },
  { key: 'target', header: 'Form / URL' },
  { key: 'opens', header: 'Opens in', className: 'w-36' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_LINK_COLUMNS = LINK_COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function linkTitle(l: PortalLink): string {
  return l.title.trim() || `Link #${l.id}`;
}

export function PatientPortalAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [data, setData] = useState<PortalAdmin | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const t = useTrackedForm<PortalSettings>(() => EMPTY);
  const { reset } = t;

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        const d = await getPortalAdmin();
        setData(d);
        if (!quiet && d.settings) reset(d.settings);
      } catch (err) {
        toast.error(getErrorMessage(err, 'Failed to load the patient portal'));
      } finally {
        setLoading(false);
      }
    },
    [reset],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const configured = data?.configured === true;

  async function save() {
    if (locked) return;
    setSaving(true);
    try {
      await savePortalSettings(t.form);
      t.reset(t.form);
      toast.success('Patient portal saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The patient portal could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  // Locked: nothing can change, so there is no Save or Revert.
  const actions = locked ? undefined : (
    <div className="flex flex-wrap items-center gap-3">
      {t.dirty && (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm text-blue-700">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            Unsaved changes
          </span>
          <Button
            variant="ghost"
            size="xs"
            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
            leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
            aria-label="Revert all changes"
            onClick={t.revertAll}
          >
            Revert
          </Button>
        </div>
      )}
      <Button onClick={() => void save()} disabled={!t.dirty || !configured || loading} loading={saving}>
        Save
      </Button>
    </div>
  );

  return (
    <div className="flex h-full flex-col overflow-hidden p-6">
      <div className="flex-shrink-0">
        <PageHeader title="Patient Portal" subtitle="The portal patients sign in to: its invitation, footer, disclaimer and links" actions={actions} />
      </div>
      {loading ? (
        <SpinnerOverlay label="Loading the patient portal…" />
      ) : !data ? (
        <p className="py-16 text-center text-sm text-slate-500">The patient portal could not be loaded. Reload the page to try again.</p>
      ) : !configured ? (
        <p className="max-w-xl rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {locked
            ? 'The patient portal is not set up for this practice yet.'
            : 'The patient portal is not set up for this practice yet. Contact Prospera support to turn it on; its settings can be changed here after that.'}
        </p>
      ) : (
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-6">
          <AdminLockedFieldset>
            <section className="max-w-3xl space-y-4 rounded-lg border border-slate-200 bg-white p-5">
              <DialogField label="Status" htmlFor="portal-status" badge={t.badge('enabled', 'Status')}>
                <Select<string>
                  id="portal-status"
                  options={ON_OFF}
                  value={t.form.enabled ? 'on' : 'off'}
                  onChange={(v) => t.patch({ enabled: v === 'on' })}
                />
              </DialogField>
              <DialogField label="Invitation Subject" htmlFor="portal-invite-subject" badge={t.badge('inviteSubject', 'Invitation Subject')}>
                <Input
                  id="portal-invite-subject"
                  value={t.form.inviteSubject}
                  maxLength={INVITE_SUBJECT_MAX}
                  onChange={(e) => t.patch({ inviteSubject: e.target.value })}
                />
              </DialogField>
              <DialogField label="Invitation Message" badge={t.badge('inviteMessage', 'Invitation Message')}>
                <RichTextEditor id="portal-invite-message" value={t.form.inviteMessage} onChange={(inviteMessage) => t.patch({ inviteMessage })} shortcodes />
              </DialogField>
              <DialogField label="Portal Footer" htmlFor="portal-footer" badge={t.badge('footerContent', 'Portal Footer')}>
                <Textarea id="portal-footer" rows={3} value={t.form.footerContent} onChange={(e) => t.patch({ footerContent: e.target.value })} />
              </DialogField>
              <DialogField label="Welcome Disclaimer" htmlFor="portal-disclaimer-active" badge={t.badge('welcDisclaimerActive', 'Welcome Disclaimer')}>
                <Select<string>
                  id="portal-disclaimer-active"
                  options={ON_OFF}
                  value={t.form.welcDisclaimerActive ? 'on' : 'off'}
                  onChange={(v) => t.patch({ welcDisclaimerActive: v === 'on' })}
                />
              </DialogField>
              <DialogField label="Disclaimer Message" htmlFor="portal-disclaimer" badge={t.badge('welcDisclaimer', 'Disclaimer Message')}>
                <Textarea id="portal-disclaimer" rows={3} value={t.form.welcDisclaimer} onChange={(e) => t.patch({ welcDisclaimer: e.target.value })} />
              </DialogField>
              <DialogField label="Verification Required" description="What a patient confirms when signing in.">
                <div className="flex flex-wrap gap-6">
                  <Checkbox
                    id="portal-dob-required"
                    label="Date of Birth"
                    checked={t.form.dobRequired}
                    onChange={(e) => t.patch({ dobRequired: e.target.checked })}
                  />
                  <Checkbox
                    id="portal-cell-required"
                    label="Cell Phone"
                    checked={t.form.cellphoneRequired}
                    onChange={(e) => t.patch({ cellphoneRequired: e.target.checked })}
                  />
                </div>
              </DialogField>
            </section>
          </AdminLockedFieldset>

          <PortalLinks data={data} onChanged={() => void load(true)} setData={setData} />
        </div>
      )}
    </div>
  );
}

function PortalLinks({
  data,
  onChanged,
  setData,
}: {
  data: PortalAdmin;
  onChanged: () => void;
  setData: (d: PortalAdmin) => void;
}) {
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<PortalLink | 'new' | null>(null);
  const [deleting, setDeleting] = useState<PortalLink | null>(null);
  const sensors = useAdminDragSensors();
  const locked = useAdminPageLevel() === 'locked';

  const rows = useMemo(
    () => data.links.filter((l) => showDeleted || l.active).sort((a, b) => a.order - b.order || a.id - b.id),
    [data.links, showDeleted],
  );
  // Locked: no drag, so no grip column.
  const canDrag = !showDeleted && !locked;
  const formLabel = (value: string) => data.formChoices.find((c) => c.value === value)?.label ?? value;

  async function handleDragEnd({ active, over }: DragEndEvent) {
    if (locked || !over || active.id === over.id) return;
    const from = rows.findIndex((l) => l.id === active.id);
    const to = rows.findIndex((l) => l.id === over.id);
    if (from < 0 || to < 0) return;
    const before = data;
    const moved = arrayMove(rows, from, to).map((l, i) => ({ ...l, order: i + 1 }));
    setData({ ...data, links: [...moved, ...data.links.filter((l) => !l.active)] });
    try {
      await reorderPortalLinks(moved.map((l) => l.id));
    } catch (err) {
      setData(before);
      toast.error(getErrorMessage(err, 'The new order could not be saved.'));
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deletePortalLink(deleting.id);
      toast.success('Link deleted');
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The link could not be deleted.'));
    }
  }

  async function handleRestore(l: PortalLink) {
    try {
      await restorePortalLink(l.id);
      toast.success('Link restored');
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The link could not be restored.'));
    }
  }

  return (
    <section className="max-w-3xl rounded-lg border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-3">
        <h2 className="text-sm font-semibold text-slate-800">Portal Links</h2>
        <div className="flex items-center gap-4">
          <Checkbox
            id="portal-links-show-deleted"
            label="Show deleted"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
          />
          {!locked && (
            <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
              Add link
            </Button>
          )}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-500">No links yet.</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
          <AdminListTable
            columns={locked ? LOCKED_LINK_COLUMNS : canDrag ? [ADMIN_DRAG_COLUMN, ...LINK_COLUMNS] : LINK_COLUMNS}
            aria-label="Portal links"
          >
            <SortableContext items={rows.map((l) => l.id)} strategy={verticalListSortingStrategy}>
              {rows.map((l) => {
                const deleted = !l.active;
                const title = linkTitle(l);
                return (
                  <SortableAdminRow
                    key={l.id}
                    id={l.id}
                    title={title}
                    canDrag={canDrag}
                    className={adminRowClass({ deleted, clickable: !deleted })}
                    onClick={deleted ? undefined : () => setEditing(l)}
                  >
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowName deleted={deleted} nameTestId="portal-link-title">
                        {title}
                      </AdminRowName>
                    </td>
                    <td className={`${ADMIN_TD_CLASS} max-w-xs truncate text-slate-600`}>{l.form ? formLabel(l.form) : l.url}</td>
                    <td className={`${ADMIN_TD_CLASS} text-slate-600`}>{l.newWindow ? 'new window' : 'same window'}</td>
                    {!locked && (
                      <td className={ADMIN_TD_CLASS}>
                        <AdminRowActions>
                          {deleted ? (
                            <AdminRowAction kind="restore" label={`Restore ${title}`} onClick={() => void handleRestore(l)} />
                          ) : (
                            <>
                              <AdminRowAction kind="edit" label={`Edit ${title}`} onClick={() => setEditing(l)} />
                              <AdminRowAction kind="delete" label={`Delete ${title}`} onClick={() => setDeleting(l)} />
                            </>
                          )}
                        </AdminRowActions>
                      </td>
                    )}
                  </SortableAdminRow>
                );
              })}
            </SortableContext>
          </AdminListTable>
        </DndContext>
      )}

      {editing && (
        <PortalLinkFormModal
          key={editing === 'new' ? 'new' : editing.id}
          link={editing === 'new' ? null : editing}
          formChoices={data.formChoices}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${linkTitle(deleting)}"?` : ''}
        message="It will no longer show on the portal. You can restore it from Show deleted."
        confirmLabel="Delete link"
        variant="danger"
      />
    </section>
  );
}

function PortalLinkFormModal({
  link,
  formChoices,
  onClose,
  onSaved,
}: {
  link: PortalLink | null;
  formChoices: { value: string; label: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = link === null;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm(
    () => ({
      title: link?.title ?? '',
      url: link?.url ?? '',
      form: link?.form ?? '',
      content: link?.content ?? '',
      newWindow: link?.newWindow ?? false,
    }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);
  const missing = t.form.title.trim() === '';
  // A stored Form that is no longer offered stays a choice of its own.
  const choices = [
    { value: '', label: 'None' },
    ...formChoices,
    ...(t.form.form && !formChoices.some((c) => c.value === t.form.form) ? [{ value: t.form.form, label: `${t.form.form} (not found)` }] : []),
  ];

  async function save() {
    if (missing || locked) return;
    setSaving(true);
    try {
      if (link) await updatePortalLink(link.id, t.form);
      else await createPortalLink(t.form);
      toast.success(isNew ? 'Link created' : 'Link updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The link could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Link' : 'Edit Link'}
      description="A Form opens that form on the portal; otherwise the link opens its URL."
      isNew={isNew}
      dirty={isNew ? !missing || t.form.url.trim() !== '' : t.dirty}
      saving={saving}
      errors={missing ? ['Title is required'] : []}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
    >
      <div className="grid gap-4">
        <DialogField
          label="Title"
          htmlFor="portal-link-title"
          required
          state={missing ? 'error' : 'none'}
          labelHint={missing ? '— required' : undefined}
          badge={t.badge('title', 'Title')}
        >
          <Input
            id="portal-link-title"
            value={t.form.title}
            maxLength={PORTAL_LINK_TITLE_MAX}
            onChange={(e) => t.patch({ title: e.target.value })}
            className={fieldClasses.input(missing ? 'error' : 'none')}
          />
        </DialogField>
        <DialogField label="URL" htmlFor="portal-link-url" badge={t.badge('url', 'URL')}>
          <Input id="portal-link-url" value={t.form.url} maxLength={2000} placeholder="https://..." onChange={(e) => t.patch({ url: e.target.value })} />
        </DialogField>
        <DialogField label="Form" htmlFor="portal-link-form" badge={t.badge('form', 'Form')}>
          <Select<string> id="portal-link-form" options={choices} value={t.form.form} onChange={(v) => t.patch({ form: v ?? '' })} />
        </DialogField>
        <DialogField label="Custom Content" htmlFor="portal-link-content" badge={t.badge('content', 'Custom Content')} description="% and $ are taken out when it is saved, as legacy did.">
          <Textarea id="portal-link-content" rows={4} value={t.form.content} onChange={(e) => t.patch({ content: e.target.value })} />
        </DialogField>
        <DialogField label="Opens in" htmlFor="portal-link-opens" badge={t.badge('newWindow', 'Opens in')}>
          <Select<string>
            id="portal-link-opens"
            options={OPENS_IN}
            value={t.form.newWindow ? 'new' : 'same'}
            onChange={(v) => t.patch({ newWindow: v === 'new' })}
          />
        </DialogField>
      </div>
    </AdminFormModal>
  );
}
